import React, { useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import isNil from 'lodash/isNil';
import moment from 'moment';
import PlacementExportMenu from 'src/components/shared/placementExportMenu';
import PlacementUpdateMenu from 'src/components/shared/placementUpdateMenu';
import { TRANSFER_PENDING } from 'src/config/AccommodationStatusNames';
import * as LDAP from 'src/config/LDAP';
import { RTW_SYSTEM } from 'src/constants';
import { setJmrFlowType } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import {
  setIsConfirmDeleteModalOpen,
  setIsEmailModalOpen,
} from 'src/store/placementDetailsPageSlice/placementDetailsPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { constructIncidentLink, constructRosieCaseLink } from 'src/utils/constructRTWLinks';
import { stripOutLeadingHash } from 'src/utils/helpers';
import { CaseSources, JmrFlowTypes } from 'src/utils/jmrUtils';

/**
 * Default component
 */
const TopOptions = () => {
  const { placementId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const updateButtonRef = useRef<HTMLButtonElement>(null);
  const { ldap } = useAppSelector((state) => state.user);
  const { consolidatedRegionTasks } = useAppSelector((state) => state.newJmrPage);
  const {
    placementDetails,
    accommodationStatus: accommodationStatusesForSite,
    placementSite,
  } = useAppSelector((state) => state.placementDetailsPage);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isUpdateMenuOpen, setIsUpdateMenuOpen] = useState(false);
  const isAdmin = ldap.includes(LDAP.ADMIN);

  return (
    <Row maxWidth="100%" alignmentVertical="top" widths={['fit', 'fill']}>
      <Column spacing="100">
        <Row>
          {/* Associate/Placement Info */}
          <Text type="h400" data-cy="placement-details__name-header">
            Case ID: {placementDetails.accommodationType} |{' '}
            {!isNil(placementDetails.externalCaseId) && placementDetails.accommodationType === CaseSources.Austin ? (
              <Tooltip title="Open case in AUSTIN" position="bottom">
                <Link
                  href={constructIncidentLink(stripOutLeadingHash(placementDetails.externalCaseId))}
                  target="_blank"
                  rel="noopener noreferrer"
                  type="secondary"
                >
                  {placementDetails.externalCaseId}
                </Link>
              </Tooltip>
            ) : (
              placementDetails.externalCaseId
            )}
          </Text>
          <Tag type="warning" data-cy="placement-details__status-tag">
            {placementDetails?.accommodationStatus?.status}
          </Tag>
        </Row>
        <Row spacing="200">
          {placementSite?.participatingInDecisionHubPilot &&
            placementDetails.accommodationType === CaseSources.Austin &&
            placementDetails.decisionHubLink && (
              <>
                <Tooltip position="bottom" title="Access limited to L6+ decision makers">
                  <Text type="b300">
                    <Link
                      type="secondary"
                      href={placementDetails.decisionHubLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Decision Hub
                    </Link>
                  </Text>
                </Tooltip>
                <Text type="b300" color="secondary">
                  •
                </Text>
              </>
            )}
          {placementDetails.accommodationType === CaseSources.Austin && (
            <>
              <Text type="b300">
                <Link type="secondary" href={constructRosieCaseLink(placementDetails.externalCaseId)}>
                  Rosie
                </Link>
              </Text>
              <Text type="b300" color="secondary">
                •
              </Text>
            </>
          )}
          <Text type="b300" color="secondary">
            Last updated: {moment(placementDetails.updatedAt).format('MM/DD/YYYY [at] h:mma')} UTC by{' '}
            {placementDetails.editedBy === RTW_SYSTEM ? (
              placementDetails.editedBy
            ) : (
              <Link
                href={`https://phonetool.amazon.com/users/${placementDetails.editedBy}`}
                target="_blank"
                rel="noopener noreferrer"
                type="secondary"
              >
                {placementDetails.editedBy}@
              </Link>
            )}
          </Text>
        </Row>
      </Column>
      <Row spacing="300" alignmentHorizontal="end">
        {/* Delete Placement */}
        {isAdmin && (
          <Button
            data-cy="placement-details__delete-btn"
            type="link"
            onClick={() => dispatch(setIsConfirmDeleteModalOpen(true))}
          >
            Delete
          </Button>
        )}

        {/* Send Email/Update/Export */}
        {placementDetails?.accommodationStatus?.status !== TRANSFER_PENDING && (
          <>
            <Button
              type="secondary"
              onClick={() => dispatch(setIsEmailModalOpen(true))}
              data-cy="placement-detail-send-email__btn"
            >
              Send Email
            </Button>
            <Button type="primary" ref={exportButtonRef} onClick={() => setIsExportMenuOpen(true)}>
              Export
              <Icon tokens={chevronDownSmallTokens} />
            </Button>
            <Button
              id="update-placement-open-menu-btn"
              data-cy="placement-detail__update-btn"
              type="primary"
              ref={updateButtonRef}
              onClick={() => setIsUpdateMenuOpen(true)}
            >
              Update
              <Icon tokens={chevronDownSmallTokens} />
            </Button>
          </>
        )}

        {/* Accept Transfer */}
        {placementDetails?.accommodationStatus?.status === TRANSFER_PENDING && (
          <Button
            type="primary"
            data-cy="placement-details__accept-transfer-btn"
            onClick={() => {
              dispatch(setJmrFlowType(JmrFlowTypes.CompleteTransfer));
              navigate(`/newJMR/${placementId}/update/${JmrFlowTypes.CompleteTransfer}`);
            }}
          >
            Accept Transfer
          </Button>
        )}

        <PlacementUpdateMenu
          isMenuOpen={isUpdateMenuOpen}
          closeMenu={() => setIsUpdateMenuOpen(false)}
          placementId={placementId!}
          anchorRef={updateButtonRef}
          accommodationStatusesForSite={accommodationStatusesForSite ?? []}
          managerAlias={placementDetails.managerAlias}
          fc={placementDetails.fc}
          accommodationType={placementDetails.accommodationType}
          placementStatus={placementDetails.accommodationStatus?.status}
          workRestrictions={placementDetails.workRestrictions}
          modifiedShiftLength={placementDetails.modifiedShiftLength}
          externalCaseId={placementDetails.externalCaseId}
          regionTasks={consolidatedRegionTasks}
        />

        <PlacementExportMenu
          isMenuOpen={isExportMenuOpen}
          closeMenu={() => setIsExportMenuOpen(false)}
          placementId={placementId!}
          anchorRef={exportButtonRef}
          newestJmrFileName={placementDetails.newestJmrFileName}
          countryId={placementDetails.countryId}
          allocatedJobShifts={placementDetails.allocatedJobShifts}
          jobModificationsEnabled={placementSite?.jobModificationsEnabled}
        />
      </Row>
    </Row>
  );
};

export default TopOptions;
